import Link from "next/link";

import { ClanCastleTroops } from "@/components/base/ClanCastleTroops";
import { shouldShowBaseAds } from "@/lib/ads/ads";
import type { BaseWithRelations } from "@/types/base";

import styles from "./BaseArticle.module.css";
import BaseSimpleDisplayAd from "./BaseSimpleDisplayAd";
import BaseArticleInlineAd from "./BaseArticleInlineAd";
import NitroMobileBaseAd from "@/components/nitro/NitroMobileBaseAd";
import { NITRO_ENABLED, ADSTERRA_ENABLED, JOURNEY_ENABLED, ADSENSE_INLINE_ONLY } from "@/components/nitro/config";
import { AdsterraBanner } from "@/components/adsterra/AdsterraAds";
import BasePostCtaMobileAd from "./BasePostCtaMobileAd";
import BaseTabletDesktopBannerAd from "./BaseTabletDesktopBannerAd";
import BaseTabletMidBannerAd from "./BaseTabletMidBannerAd";

type Props = {
  base: BaseWithRelations;
  showInlineAds?: boolean;
};

function getStrengthTitle(relation: NonNullable<BaseWithRelations["base_strengths"]>[number]) {
  return relation.title?.trim() || relation.strength?.name || "Strength";
}

export default function BaseArticle({
  base,
}: Props) {
  const legacyStrengths = base.details?.strengths ?? [];
  const semanticStrengths = (base.base_strengths ?? [])
    .filter((relation) => relation.is_active && relation.strength?.is_active)
    .sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return (a.position ?? 0) - (b.position ?? 0);
    });
  const strengths = semanticStrengths.length > 0 ? [] : legacyStrengths;
  const cc = base.details?.clan_castle_troops ?? [];
  const tags = base.content_tier === "utility" ? [] : (base.tags ?? []);
  const showAds = shouldShowBaseAds(base) && !JOURNEY_ENABLED && !ADSENSE_INLINE_ONLY;
  const shareLink = base.share_link;

  const hasStrengthContent = strengths.length > 0 || semanticStrengths.length > 0;
  const hasMetaContent = cc.length > 0 || tags.length > 0;

  return (
    <article className={styles.article}>
      {showAds && ADSTERRA_ENABLED ? <AdsterraBanner format="slim" position="mobile-entry" /> : null}
      {showAds && ADSTERRA_ENABLED ? <AdsterraBanner format="desktop" position="desktop-header" /> : null}
      {showAds ? (
        <BaseTabletDesktopBannerAd
          targeting={{
            level: base.level?.slug,
            category: base.category?.slug,
            base_id: base.id,
          }}
        />
      ) : null}


      {hasStrengthContent ? (
        <div className="py-sm">
          <section className={styles.block}>
            <div className="stack-sm">
              <h2 className={`heading-md ${styles.sectionTitle}`}>Strengths</h2>

              {semanticStrengths.length > 0 ? (
                <ul className={styles.strengthList}>
                  {semanticStrengths.map((relation) => {
                    const strength = relation.strength;
                    const title = getStrengthTitle(relation);
                    const description = relation.description?.trim() || strength?.description?.trim() || "";

                    return (
                     <li key={relation.id} className={styles.strengthItem}>
                      <div className={styles.strengthContent}>
                        {strength ? (
                          <Link
                            href={`/strengths/${strength.slug}/${base.level.slug}`}
                            className={styles.strengthTitleLink}
                            title={`${strength.name} Clash of Clans bases`}
                          >
                            <h3 className={styles.strengthTitle}>
                              {title} →
                            </h3>
                          </Link>
                        ) : (
                          <h3 className={styles.strengthTitle}>{title}</h3>
                        )}

                        {description ? (
                          <p className={styles.strengthDescription}>
                            {description}
                          </p>
                        ) : null}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              ) : null}

              {strengths.length > 0 ? (
                <ul className={styles.strengthList}>
                  {strengths.map((strength, index) => (
                    <li
                      key={`${strength.title}-${index}`}
                      className={styles.strengthItem}
                    >
                      <div className={styles.strengthContent}>
                        <h3 className={styles.strengthTitle}>{strength.title}</h3>

                        {strength.description ? (
                          <p className={styles.strengthDescription}>
                            {strength.description}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {showAds && hasStrengthContent ? (
        <BaseTabletMidBannerAd
          targeting={{
            level: base.level?.slug,
            category: base.category?.slug,
            base_id: base.id,
          }}
        />
      ) : null}

      {showAds ? (
        <div className={styles.premiumAdSlot}>
          {ADSTERRA_ENABLED ? (
            <AdsterraBanner format="square" position="mobile-content" />
          ) : NITRO_ENABLED ? (
            <NitroMobileBaseAd />
          ) : (
            <BaseSimpleDisplayAd
              targeting={{
                level: base.level?.slug,
                category: base.category?.slug,
                base_id: base.id,
              }}
            />
          )}
        </div>
      ) : null}

      {hasMetaContent ? (
        <div className="py-sm">
          <section className={styles.block}>
            <div className="stack-sm">
              <div className={styles.metaGrid}>
                {cc.length > 0 ? (
                  <div className={styles.ccColumn}>
                    <div className="stack-xs">
                      <h2 className={`heading-sm ${styles.subTitle}`}>
                        Clan Castle Troops
                      </h2>
                      <ClanCastleTroops troops={cc} />
                    </div>
                  </div>
                ) : null}

                {tags.length > 0 ? (
                  <div className={styles.tagsColumn}>
                    <div className="stack-xs">
                      <h2 className={`heading-sm ${styles.subTitle}`}>Tags</h2>

                      <div className={styles.tagGrid}>
                        {tags.map((tag) => (
                          <span key={tag} className={styles.tagPill}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {shareLink ? (
        <div>
          <div className={styles.openWrap}>
            <a
              href={shareLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.openBtn}
            >
              Open in Clash of Clans
            </a>
          </div>

          {showAds && ADSTERRA_ENABLED ? <AdsterraBanner format="square" position="mobile-post-cta" /> : null}
          {showAds ? (
            <BasePostCtaMobileAd
              targeting={{
                level: base.level?.slug,
                category: base.category?.slug,
                base_id: base.id,
              }}
            />
          ) : null}
        </div>
      ) : null}
      {ADSENSE_INLINE_ONLY && shouldShowBaseAds(base) ? (
        <BaseArticleInlineAd key={base.id} />
      ) : null}
    </article>
  );
}
